import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "../client";
import {
  SceneCreate,
  SceneRead,
  SceneUpdate,
  type SceneCreate as SceneCreateT,
  type SceneUpdate as SceneUpdateT,
} from "../schemas";
import { qk } from "../queryKeys";

const SceneList = z.array(SceneRead);

export function useScenes(projectId: number) {
  return useQuery({
    queryKey: qk.scenes(projectId),
    queryFn: () => api.get(`/api/projects/${projectId}/scenes`, SceneList),
    enabled: projectId > 0,
  });
}

export function useScene(id: number | undefined) {
  return useQuery({
    queryKey: id ? qk.scene(id) : qk.scene(0),
    queryFn: () => api.get(`/api/scenes/${id}`, SceneRead),
    enabled: typeof id === "number" && id > 0,
  });
}

export function useCreateScene(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SceneCreateT) =>
      api.post(`/api/projects/${projectId}/scenes`, SceneCreate.parse(input), SceneRead),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.scenes(projectId) }),
  });
}

export function useUpdateScene(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: SceneUpdateT }) =>
      api.patch(`/api/scenes/${id}`, SceneUpdate.parse(patch), SceneRead),
    onSuccess: (next) => {
      qc.setQueryData(qk.scene(next.id), next);
      qc.invalidateQueries({ queryKey: qk.scenes(projectId) });
    },
  });
}

export function useDeleteScene(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/scenes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.scenes(projectId) }),
  });
}
